
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../atoms/Button';

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    'farmtrace': true,
    'energy': false,
    'solar': true
  });

  useEffect(() => {
    document.body.dataset.sidebar = isCollapsed ? 'collapsed' : 'expanded';
  }, [isCollapsed]);

  // Auto-expand parent menus when a child is active
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpandedState: Record<string, boolean> = {};

    // Function to check if any child is active and build the parent chain
    const checkAndExpandParents = (items: any[], parentId?: string) => {
      items.forEach(item => {
        const hasActiveChild = item.path && currentPath === item.path;
        const hasActiveGrandChild = item.children?.some((child: any) =>
          child.path && currentPath === child.path ||
          child.children?.some((grandChild: any) => grandChild.path && currentPath === grandChild.path)
        );

        if (hasActiveChild || hasActiveGrandChild) {
          // Expand this item and its parent
          newExpandedState[item.id] = true;
          if (parentId) {
            newExpandedState[parentId] = true;
          }
        }

        // Recursively check children
        if (item.children) {
          checkAndExpandParents(item.children, item.id);
        }
      });
    };

    checkAndExpandParents(menuItems);

    // Replace the entire state (close everything not in active path)
    setExpandedMenus(newExpandedState);
  }, [location.pathname]);

  const toggleMenu = (id: string) => {
    setExpandedMenus(prev => {
      const isCurrentlyExpanded = prev[id];
      const newExpandedState: Record<string, boolean> = {};

      // Helper function to find parent chain
      const findParentChain = (targetId: string, items: any[], chain: string[] = []): string[] | null => {
        for (const item of items) {
          if (item.id === targetId) {
            return [...chain, targetId];
          }
          if (item.children) {
            const result = findParentChain(targetId, item.children, [...chain, item.id]);
            if (result) return result;
          }
        }
        return null;
      };

      // If expanding a menu, keep only this menu and its parents open
      if (!isCurrentlyExpanded) {
        const parentChain = findParentChain(id, menuItems);
        if (parentChain) {
          parentChain.forEach(menuId => {
            newExpandedState[menuId] = true;
          });
        }
      }
      // If closing, close everything (newExpandedState is already empty)

      return newExpandedState;
    });
  };

  const menuItems = [
    {
      id: 'ft-overview',
      label: 'Resumen Ejecutivo',
      path: '/farmtrace/overview',
      icon: 'ri-dashboard-line',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50'
    },
    {
      id: 'farmtrace',
      label: 'Campos Smart Granja',
      icon: 'ri-drop-line',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      children: [
        { id: 'ft-production', label: 'Producción', path: '/farmtrace/production', icon: 'ri-bar-chart-line' },
        { id: 'ft-health', label: 'Salud del Ganado', path: '/farmtrace/health', icon: 'ri-heart-pulse-line' },
        { id: 'ft-efficiency', label: 'Eficiencia', path: '/farmtrace/efficiency', icon: 'ri-speed-line' },
        { id: 'ft-operations', label: 'Operaciones', path: '/farmtrace/operations', icon: 'ri-map-pin-line' },
        { id: 'ft-profitability', label: 'Rentabilidad', path: '/farmtrace/profitability', icon: 'ri-money-dollar-circle-line' },
        { id: 'ft-reports', label: 'Reportes', path: '/farmtrace/reports', icon: 'ri-file-list-line' },
      ]
    },
    {
      id: 'energy',
      label: 'Energía',
      icon: 'ri-flashlight-line',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      children: [
        {
          id: 'solar',
          label: 'Campos Smart Solar',
          icon: 'ri-sun-line',
          children: [
            { id: 'home', path: '/home', icon: 'ri-home-4-line', label: 'Inicio' },
            { id: 'powerflow', path: '/powerflow', icon: 'ri-exchange-line', label: 'Flujo de Energía' },
            { id: 'energy-meter', path: '/energy', icon: 'ri-flashlight-line', label: 'Medidor de Energía' },
            { id: 'inverter', path: '/inverter', icon: 'ri-cpu-line', label: 'Estado del Inversor' },
            { id: 'energy-today', path: '/energy-today', icon: 'ri-sun-line', label: 'Energía Hoy' },
            { id: 'history', path: '/history', icon: 'ri-bar-chart-box-line', label: 'Histórico' },
            { id: 'devices', path: '/devices', icon: 'ri-server-line', label: 'Plantas e Inversores' },
            { id: 'reports', path: '/reports', icon: 'ri-file-chart-line', label: 'Reportes' },
            { id: 'settings', path: '/settings', icon: 'ri-settings-3-line', label: 'Configuración' },
            { id: 'profile', path: '/profile', icon: 'ri-user-line', label: 'Mi Perfil' }
          ]
        },
        {
          id: 'smart-meter',
          label: 'Smart Meter',
          icon: 'ri-dashboard-2-line',
          path: '/smart-meter'
        }
      ]
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem('fronius_user');
    localStorage.removeItem('fronius_auth');
    localStorage.removeItem('is_authenticated');
    navigate('/login');
  };

  const renderMenuItem = (item: any, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.id];
    const isActive = item.path && location.pathname === item.path;
    const isParentActive = hasChildren && item.children.some((child: any) =>
      (child.path && location.pathname === child.path) ||
      (child.children && child.children.some((grandChild: any) => (grandChild.path && location.pathname === grandChild.path)))
    );

    // Determine styling based on depth
    const isTopLevel = depth === 0;
    const isSecondLevel = depth === 1;

    return (
      <div key={item.id} className={`${isTopLevel ? 'mb-2' : 'mb-0.5'}`}>
        {hasChildren ? (
          <button
            onClick={() => !isCollapsed && toggleMenu(item.id)}
            className={`
              w-full flex items-center rounded-xl transition-all duration-200 group relative overflow-hidden
              ${isCollapsed ? 'justify-center px-4 py-3' : 'justify-between'}
              ${isTopLevel ? 'px-4 py-3' : isSecondLevel ? 'px-3 py-2.5 ml-4' : 'px-3 py-2 ml-8'}
              ${isParentActive
                ? isTopLevel
                  ? `${item.bgColor || 'bg-blue-50'} ${item.color || 'text-blue-600'} shadow-md border-l-4 ${item.color?.replace('text-', 'border-') || 'border-blue-600'} font-bold`
                  : 'bg-gray-100 text-gray-900 font-semibold border-l-3 border-gray-400'
                : isTopLevel
                  ? 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }
            `}
            title={isCollapsed ? item.label : ''}
          >
            {/* Active indicator bar */}
            {isParentActive && !isCollapsed && (
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${item.color?.replace('text-', 'bg-') || 'bg-blue-600'} rounded-r-full`}></div>
            )}

            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
              <i className={`
                ${item.icon} 
                ${isTopLevel ? 'text-2xl' : isSecondLevel ? 'text-lg' : 'text-base'}
                transition-transform group-hover:scale-110 
                ${isParentActive
                  ? (item.color || 'text-blue-600')
                  : isTopLevel
                    ? 'text-gray-500 group-hover:text-gray-700'
                    : 'text-gray-400 group-hover:text-gray-600'
                }
              `}></i>
              {!isCollapsed && (
                <span className={`
                  truncate
                  ${isTopLevel ? 'text-sm font-semibold' : isSecondLevel ? 'text-sm' : 'text-xs'}
                  ${isParentActive ? 'font-bold' : ''}
                `}>
                  {item.label}
                </span>
              )}
            </div>

            {!isCollapsed && (
              <div className="flex items-center gap-2">
                {isParentActive && (
                  <div className={`w-2 h-2 rounded-full ${item.color?.replace('text-', 'bg-') || 'bg-blue-600'} animate-pulse`}></div>
                )}
                <i className={`
                  ri-arrow-down-s-line 
                  transition-transform duration-200 
                  ${isExpanded ? 'rotate-180' : ''} 
                  ${isParentActive ? (item.color || 'text-blue-600') : 'text-gray-400'}
                  ${isTopLevel ? 'text-lg' : 'text-base'}
                `}></i>
              </div>
            )}
          </button>
        ) : (
          <Link
            to={item.path}
            className={`
              flex items-center rounded-xl transition-all duration-200 group relative overflow-hidden
              ${isCollapsed ? 'justify-center px-4 py-3' : 'space-x-3'} 
              ${isTopLevel ? 'px-4 py-3' : isSecondLevel ? 'px-3 py-2.5 ml-4' : 'px-3 py-2 ml-8'}
              ${isActive
                ? isTopLevel
                  ? 'bg-blue-600 text-white shadow-lg font-bold'
                  : isSecondLevel
                    ? 'bg-blue-50 text-blue-700 shadow-md border-l-4 border-blue-600'
                    : 'bg-blue-50 text-blue-600 shadow-sm font-medium border-l-3 border-blue-500'
                : isTopLevel
                  ? 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }
            `}
            title={isCollapsed ? item.label : ''}
          >
            {/* Active indicator bar for non-top-level items */}
            {isActive && !isTopLevel && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-full"></div>
            )}

            <i className={`
              ${item.icon} 
              ${isTopLevel ? 'text-2xl' : isSecondLevel ? 'text-lg' : 'text-base'}
              transition-transform group-hover:scale-110 
              ${isActive
                ? isTopLevel ? 'text-white' : 'text-blue-600'
                : isTopLevel
                  ? 'text-gray-500 group-hover:text-gray-700'
                  : 'text-gray-400 group-hover:text-gray-600'
              }
            `}></i>

            {!isCollapsed && (
              <span className={`
                truncate
                ${isTopLevel ? 'text-sm font-semibold' : isSecondLevel ? 'text-sm' : 'text-xs'}
                ${isActive ? 'font-bold' : ''}
              `}>
                {item.label}
              </span>
            )}

            {/* Active indicator dot */}
            {isActive && !isCollapsed && (
              <div className={`w-2 h-2 rounded-full ${isTopLevel ? 'bg-white' : 'bg-blue-600'} ml-auto`}></div>
            )}
          </Link>
        )}

        {hasChildren && isExpanded && !isCollapsed && (
          <div className={`
            mt-1 space-y-0.5
            ${isTopLevel ? 'pl-0' : 'pl-2'}
            ${isTopLevel ? 'border-l-2 border-gray-100 ml-6' : ''}
          `}>
            {item.children.map((child: any) => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`w-[var(--sidebar-width)] bg-white border-r border-gray-200 h-screen flex flex-col transition-all duration-300 fixed left-0 top-0 z-40 shadow-xl`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center space-x-3 animate-fade-in">
              <img
                src="/images/logo.png"
                alt="Logo"
                className="h-10 object-contain transform hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="ghost"
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
            aria-label="Alternar menú"
          >
            <i className={`${isCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl`}></i>
          </Button>
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
        {menuItems.map((item) => renderMenuItem(item))}
      </nav>

      {/* User Info & Actions */}
      <div className="p-4 border-t border-gray-100 bg-gray-50/50">
        {!isCollapsed && (
          <div className="mb-4 p-3 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white shadow-md">
              <span className="font-bold text-sm">
                {(localStorage.getItem('fronius_user') || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {localStorage.getItem('fronius_user') || 'Usuario'}
              </p>
              <div className="flex items-center space-x-1 text-xs text-green-600 font-medium">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span>Conectado</span>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={handleLogout}
          variant="secondary"
          className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start space-x-3 px-4'} py-3 bg-white hover:bg-red-50 text-gray-600 hover:text-red-600 border border-gray-200 hover:border-red-200 transition-all shadow-sm hover:shadow`}
        >
          <i className="ri-logout-box-line text-xl"></i>
          {!isCollapsed && <span className="font-medium text-sm">Cerrar Sesión</span>}
        </Button>
      </div>
    </div>
  );
}
